import { useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  isExplicitlySignedOut,
  markExplicitlySignedOut,
  SESSION_ACTIVITY_KEY,
} from '../../auth/logoutState';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_WRITE_INTERVAL_MS = 1000;

// Generated by GitHub Copilot - Oct-24-2026
/**
 * Ends an authenticated application session after 15 minutes without user activity.
 * Activity timestamps are shared across tabs; expiry uses the existing logout marker.
 */
export default function SessionExpiry() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const authStateRef = useRef({ instance, inProgress, isAuthenticated });
  const timerRef = useRef(null);
  const lastActivityRef = useRef(0);
  const lastPersistedActivityRef = useRef(0);
  const logoutStartedRef = useRef(false);
  const scheduleExpiryRef = useRef(null);

  authStateRef.current = { instance, inProgress, isAuthenticated };

  useEffect(() => {
    let disposed = false;

    /** Redirects this authenticated tab through MSAL logout once idle time expires. */
    const expireSession = () => {
      const authState = authStateRef.current;
      if (
        disposed || logoutStartedRef.current || isExplicitlySignedOut() ||
        !authState.isAuthenticated || authState.inProgress !== InteractionStatus.None
      ) return;
      logoutStartedRef.current = true;
      markExplicitlySignedOut();
      authState.instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
        account: authState.instance.getActiveAccount() ?? authState.instance.getAllAccounts()[0] ?? undefined,
      }).catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[Auth] idle logout error:', error);
        }
      });
    };

    /** Schedules expiry against the last known activity timestamp. */
    const scheduleExpiry = () => {
      window.clearTimeout(timerRef.current);
      const authState = authStateRef.current;
      if (!authState.isAuthenticated || authState.inProgress !== InteractionStatus.None) return;
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - lastActivityRef.current);
      if (remaining <= 0) {
        expireSession();
        return;
      }
      timerRef.current = window.setTimeout(expireSession, remaining);
    };
    scheduleExpiryRef.current = scheduleExpiry;

    /** Reads activity shared by another tab and refreshes this tab's expiry timer. */
    const syncActivity = (timestamp) => {
      const parsedTimestamp = Number(timestamp);
      if (!Number.isFinite(parsedTimestamp) || parsedTimestamp <= lastActivityRef.current) return;
      lastActivityRef.current = parsedTimestamp;
      lastPersistedActivityRef.current = parsedTimestamp;
      scheduleExpiry();
    };

    /** Records meaningful user interaction and shares it with sibling tabs. */
    const recordActivity = () => {
      const authState = authStateRef.current;
      if (
        isExplicitlySignedOut() || !authState.isAuthenticated ||
        authState.inProgress !== InteractionStatus.None
      ) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastPersistedActivityRef.current >= ACTIVITY_WRITE_INTERVAL_MS) {
        try {
          window.localStorage.setItem(SESSION_ACTIVITY_KEY, String(now));
          lastPersistedActivityRef.current = now;
        } catch { /* The local tab timer still enforces expiry if storage is unavailable. */ }
      }
      scheduleExpiry();
    };

    /** Applies cross-tab activity updates or stops tracking after another tab logs out. */
    const handleStorage = (event) => {
      if (event.key === 'gccStudio.explicitLogout' && event.newValue) {
        window.clearTimeout(timerRef.current);
        return;
      }
      if (event.key === SESSION_ACTIVITY_KEY) syncActivity(event.newValue);
    };

    /** Stops the local timer immediately when explicit logout begins in this tab. */
    const handleLogoutState = () => {
      if (isExplicitlySignedOut()) window.clearTimeout(timerRef.current);
    };

    /** Rechecks the deadline when a backgrounded tab becomes visible again. */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleExpiry();
    };

    const activityEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, recordActivity, { passive: true }));
    window.addEventListener('storage', handleStorage);
    window.addEventListener('gccStudio:logoutStateChanged', handleLogoutState);
    window.addEventListener('pageshow', scheduleExpiry);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearTimeout(timerRef.current);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('gccStudio:logoutStateChanged', handleLogoutState);
      window.removeEventListener('pageshow', scheduleExpiry);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      scheduleExpiryRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None || isExplicitlySignedOut()) return;

    if (!lastActivityRef.current) {
      try {
        const storedActivity = Number(window.localStorage.getItem(SESSION_ACTIVITY_KEY));
        lastActivityRef.current = Number.isFinite(storedActivity) && storedActivity > 0
          ? storedActivity
          : Date.now();
        lastPersistedActivityRef.current = lastActivityRef.current;
        if (!storedActivity) {
          window.localStorage.setItem(SESSION_ACTIVITY_KEY, String(lastActivityRef.current));
        }
      } catch {
        lastActivityRef.current = Date.now();
      }
    }

    scheduleExpiryRef.current?.();
  }, [inProgress, isAuthenticated]);

  return null;
}
